/**
 * Default manuscript-viewer theme and translation helpers.
 *
 * Encapsulates the color palette so the React components stay focused on
 * layout and behavior, and the look-and-feel can be swapped per demo
 * without touching the viewer itself.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import type { MonographDemoTheme } from "../MonographDemoShell.js";
import type { ManuscriptViewerTheme } from "./types.js";
import defaultTheme from "./default-theme.data.json" with { type: "json" };

export const DEFAULT_MANUSCRIPT_THEME: ManuscriptViewerTheme = defaultTheme;

export function resolveTheme(
  partial: Partial<ManuscriptViewerTheme> | undefined,
): ManuscriptViewerTheme {
  if (partial === undefined) return DEFAULT_MANUSCRIPT_THEME;
  return { ...DEFAULT_MANUSCRIPT_THEME, ...partial };
}

/**
 * Re-export the shell's theme tokens as manuscript-viewer tokens, so a demo
 * that already builds a `MonographDemoTheme` can pass it through without
 * recomputing colors.
 */
export function manuscriptThemeFromShellTheme(
  shellTheme: MonographDemoTheme,
): Partial<ManuscriptViewerTheme> {
  return {
    void: shellTheme.void,
    surface: shellTheme.surface,
    border: shellTheme.border,
    text: shellTheme.text,
    bright: shellTheme.bright,
    muted: shellTheme.muted,
    accent: shellTheme.accent,
    serif: shellTheme.sans,
    mono: shellTheme.mono,
  };
}

export function tierColor(tier: string | undefined, theme: ManuscriptViewerTheme): string {
  if (tier === undefined) return theme.muted;
  const normalized = tier.toLowerCase();
  if (normalized === "essential") return theme.accentSoft;
  if (normalized === "deep") return theme.muted;
  if (normalized === "practical") return "#7B9E6B";
  return theme.muted;
}
